import { computed, type Ref } from "vue";

/**
 * Creates a search pattern for Fuse.js based on an object's properties
 *
 * @param source The source object containing search values
 * @param fields An array of field configurations to search on
 * @returns A computed property that returns a Fuse.js search pattern or null
 */
export function useSearchPattern<T extends Record<string, any>>(
  source: Ref<T | undefined>,
  fields: Array<{
    key: keyof T;
    transform?: (value: any) => string;
    minLength?: number;
  }>
) {
  return computed(() => {
    // Check if there's any searchable input
    const hasSearchableInput = fields.some(field => {
      const value = source.value?.[field.key];
      if (value === undefined || value === null) return false;

      const stringValue = field.transform ? field.transform(value) : String(value);
      return stringValue.trim().length >= (field.minLength ?? 1);
    });

    if (!hasSearchableInput) {
      return null;
    }

    // Create an $or pattern for better search results
    const pattern: any = {
      $or: [],
    };

    fields.forEach(field => {
      const value = source.value?.[field.key];
      if (value === undefined || value === null) return;

      const stringValue = field.transform ? field.transform(value) : String(value);
      if (stringValue.trim().length >= (field.minLength ?? 1)) {
        pattern.$or.push({ [field.key]: stringValue.trim() });
      }
    });

    return pattern;
  });
}

/**
 * Creates a standard set of Fuse.js options with customizable weights
 *
 * @param keys An array of keys with their search weights
 * @returns Fuse.js options object
 */
export function createFuseOptions(
  keys: Array<{ name: string; weight: number }>
) {
  return {
    keys,
    threshold: 0.3,
    distance: 100,
    ignoreLocation: true,
    useExtendedSearch: true,
    findAllMatches: true,
    includeScore: true,
    shouldSort: true,
    minMatchCharLength: 1,
    isCaseSensitive: false,
  };
}
